import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthUser } from '../../hooks/useAuthUser';
import spinner from '../../assets/Spinner.svg'
import timeIcon from '../../assets/timeIcon.svg'
import formatDate from '../../utils/formatDate';

import style from './GeneratedStryd.module.css';
// import StrydCardSmall from '../../components/StrydCardSmall/StrydCardSmall';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';

import { generateWorkoutRoutines } from '../../utils/generateRoutines';


const API_URL = process.env.REACT_APP_API_URL;

const GeneratedStryds = () => {
	const { user } = useAuthUser();
	const navigate = useNavigate()

	const [interests, setInterests] = useState({})
	const [routine, setRoutine] = useState({})
	const [, setIsLoading] = useState(true) // value not read; keep setter for async UI control


	// Helper: safely parse JSON from LLM responses that may include ``` fences or extra text
	const parseRoutineResponse = useCallback((text) => {
		if (!text || typeof text !== 'string') {
			throw new Error('Empty or invalid routine response');
		}

		// 1) Try to extract fenced code block first
		const fenceMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
		const candidate = (fenceMatch ? fenceMatch[1] : text).trim();

		// 2) Try JSON.parse directly
		try {
			return JSON.parse(candidate);
		} catch (_) {
			// 3) Fallback: slice between first { and last }
			const start = candidate.indexOf('{');
			const end = candidate.lastIndexOf('}');
			if (start !== -1 && end !== -1 && end > start) {
				const sliced = candidate.slice(start, end + 1);
				return JSON.parse(sliced);
			}
			// 4) Give up with original parse error context
			throw new Error('Failed to parse routine JSON');
		}
	}, []);

	// Post-processor: sanitize workouts to enforce equipment policy regardless of model output
	const sanitizeRoutine = useCallback((incomingRoutine, options) => {
		const { isBodyweightMode, allowedEquipmentLabels } = options;

		if (!incomingRoutine || typeof incomingRoutine !== 'object') return incomingRoutine;
		const safeRoutine = { ...incomingRoutine };
		const workouts = Array.isArray(incomingRoutine.workouts) ? incomingRoutine.workouts : [];

		// Canonical equipment keywords we try to detect in workout names
		const EQUIP_KEYWORDS = {
			barbell: ['barbell'],
			dumbbell: ['dumbbell', 'dumbbells', 'db '],
			kettlebell: ['kettlebell', 'kettlebells', 'kb '],
			band: ['resistance band', 'band', 'mini band', 'loop band'],
			cable: ['cable'],
			machine: ['machine', 'leg press', 'pec deck', 'smith machine'],
			bench: ['bench'],
			box: ['box jump', 'plyo box'],
			trx: ['trx', 'suspension'],
			pullupbar: ['pull-up bar', 'pull up bar', 'pullup bar'],
			medball: ['medicine ball', 'med ball', 'slam ball'],
			rower: ['rower', 'rowing machine'],
			bike: ['air bike', 'assault bike', 'bike erg', 'stationary bike'],
			treadmill: ['treadmill']
		};

		// Build allowed keyword set from labels
		const allowedKeywords = new Set();
		const labelsStr = (allowedEquipmentLabels || []).map(s => String(s).toLowerCase());
		Object.entries(EQUIP_KEYWORDS).forEach(([key, variants]) => {
			const isAllowed = labelsStr.some(lbl => lbl.includes(key) || variants.some(v => lbl.includes(v)));
			if (isAllowed && !isBodyweightMode) {
				variants.forEach(v => allowedKeywords.add(v));
				allowedKeywords.add(key);
			}
		});

		const containsDisallowedEquip = (name) => {
			const n = String(name || '').toLowerCase();
			// If bodyweight mode, ANY equipment keyword is disallowed
			if (isBodyweightMode) {
				return Object.values(EQUIP_KEYWORDS).some(variants => variants.some(v => n.includes(v)));
			}
			// Otherwise, only disallow if it contains an equipment keyword that is not allowed
			for (const variants of Object.values(EQUIP_KEYWORDS)) {
				const hits = variants.filter(v => n.includes(v));
				if (hits.length > 0) {
					const isAllowed = hits.some(h => allowedKeywords.has(h));
					if (!isAllowed) return true;
				}
			}
			return false;
		};

		const BODYWEIGHT_ALTS = [
			'Push-Ups',
			'Bodyweight Squats',
			'Walking Lunges',
			'Glute Bridges',
			'Plank',
			'Side Plank',
			'Mountain Climbers',
			'Jumping Jacks',
			'Reverse Lunges',
			'Bird Dogs',
			'Supermans',
			'Hip Thrusts (floor)',
			'Calf Raises',
			'Wall Sit',
			'Kegels',
			'Pelvic Tilts',
			'Clamshells',
			'Fire Hydrants',
			'Marching Bridges',
			'Sumo Squats'
		];

		const usedNames = new Set();
		const safeWorkouts = [];
		const defaultSets = '3 sets';
		const defaultReps = '10-15 reps';

		for (const w of workouts) {
			const type = w && w.workoutType ? w.workoutType : '';
			if (!type || containsDisallowedEquip(type)) {
				// Skip for now; we'll fill later
				continue;
			}
			if (usedNames.has(type)) continue;
			usedNames.add(type);
			safeWorkouts.push({
				workoutType: type,
				workoutSets: w.workoutSets || defaultSets,
				workoutReps: w.workoutReps || defaultReps,
			});
		}

		// Fill to exactly 5 items with bodyweight-safe alternatives if needed
		let i = 0;
		while (safeWorkouts.length < 5 && i < BODYWEIGHT_ALTS.length) {
			const alt = BODYWEIGHT_ALTS[i++];
			if (usedNames.has(alt)) continue;
			usedNames.add(alt);
			safeWorkouts.push({ workoutType: alt, workoutSets: defaultSets, workoutReps: defaultReps });
		}

		// Trim if too many
		safeRoutine.workouts = safeWorkouts.slice(0, 5);
		return safeRoutine;
	}, []);


	// Build strict equipment policy based on user selection (memoized)
	const selectedEquipments = useMemo(() => {
		const raw = interests.workoutEquipments;
		if (Array.isArray(raw)) return raw;
		if (typeof raw === 'string' && raw) {
			return raw.split(',').map(e => e.trim()).filter(Boolean);
		}
		return [];
	}, [interests.workoutEquipments]);

	const isBodyweight = useMemo(() => {
		return String(interests.type || '').toLowerCase().includes('bodyweight');
	}, [interests.type]);

	const equipmentsLabel = useMemo(() => {
		return (isBodyweight || selectedEquipments.length === 0)
			? 'None'
			: selectedEquipments.join(', ');
	}, [isBodyweight, selectedEquipments]);

	const equipmentPolicy = useMemo(() => {
		return isBodyweight
			? `Equipment policy: Strictly NO EQUIPMENT. Only bodyweight exercises are allowed. Do NOT include any exercise that requires tools such as dumbbells, barbells, kettlebells, resistance bands, cables, machines, benches, boxes, TRX/suspension trainers, pull-up bars, or medicine balls.`
			: `Equipment policy: Use ONLY the following equipment if needed and absolutely no other equipment: [${equipmentsLabel}]. If an exercise typically requires unlisted equipment, choose an alternative using only the allowed items or bodyweight.`;
	}, [isBodyweight, equipmentsLabel]);

	const prompt = useMemo(() => `You are an expert personal trainer creating a single workout routine.

<user_profile>
  - Workout Type: ${interests.type}
  - Interests: [${interests.workoutInterests}]
  - Equipment: [${equipmentsLabel}]
  - Fitness Level: ${interests.level}
  - Workout Style: ${interests.style}
  - Session Duration: ${interests.duration}
  - Age Range: ${interests.ageRange}
  - Gender: ${interests.gender}
  - Goal: ${interests.goal}
  - Additional Info: ${interests.additionalText || 'None'}
</user_profile>

${equipmentPolicy}

Rules:
1. Every workout must be a specific, named exercise (e.g., "Push-Ups", "Goblet Squats", "Dumbbell Rows") — NEVER a general category like "HIIT", "Cardio", or "Strength Training".
2. Each exercise must have clearly defined sets and reps (e.g., "3 sets", "12-15 reps").
3. Order exercises logically: compound movements first, isolation exercises later, core work last.
4. Match the total session time to the user's preferred duration.
5. Ensure exercise selection directly supports the user's stated goal.
6. Adjust intensity (sets, reps, rest) to the user's fitness level:
   - Beginner: higher reps, fewer sets, simpler movements.
   - Intermediate: moderate volume, compound + isolation mix.
   - Advanced: higher volume, complex movements, supersets where appropriate.
7. Strictly enforce the equipment policy — zero exceptions.
8. If the user's interests include "Pelvic Floor", include pelvic floor exercises such as Kegels, Pelvic Tilts, Clamshells, Fire Hydrants, Marching Bridges, Diaphragmatic Breathing, Happy Baby Pose, or Sumo Squats. Use hold durations (e.g., "5-second holds") for reps when appropriate for isometric exercises like Kegels.

Respond with ONLY a JSON object:

{
  "routineName": "3 words maximum",
  "estimatedTimeMinutes": number,
  "routineReason": "2 sentences explaining why this routine is personalized for this user",
  "workouts": [
    { "workoutType": "Specific Exercise Name", "workoutSets": "3 sets", "workoutReps": "12-15 reps" }
  ]
}

Include exactly 5 exercises. Output ONLY the JSON — no markdown fences, no extra text.
`, [
	interests.type,
	interests.workoutInterests,
	equipmentsLabel,
	interests.level,
	interests.style,
	interests.duration,
	interests.ageRange,
	interests.gender,
	interests.goal,
	interests.additionalText,
	equipmentPolicy,
]);



	const getInterests = useCallback(async () => {
		try {
			const res = await axios.get(`${API_URL}/interests/${user.id}`);
			setInterests({ ...res.data });
			console.log("got interests");
			// interests loaded
		} catch (err) {
			console.log(err);
		}
	}, [user]);

	const updatedRoutine = {
		...routine,
		createdDate: formatDate(), // Add current date in the specified format
		routineCreationType: 'generated', // Add routineCreationType
	};


	const addStryds = async () => {
		const userToAdd = {
			userID: user.id,
			username: user.username,
			profileImageLink: user.profileImageUrl,
			userRoutineXP: 0
			// Add other data needed for joining the routine
		};

		try {
			const res = await axios.post(`${API_URL}/stryds/${user.id}`, updatedRoutine);
			setIsLoading(true);
			console.log(res);

			// Add user as participant after the first request succeeds
			const response = await axios.post(
				`${API_URL}/stryds/join-routine/${user.id}/${routine.routineName}`,
				userToAdd
			);

			console.log(response.data); // Handle successful response
			// toast('joined routine')
			setIsLoading(false);
			navigate('/profile-page');
			return response.data;
		} catch (error) {
			console.error(error.response.data); // Handle error response
			setIsLoading(false);
			throw new Error(error.response.data);
		}
	};




	useEffect(() => {
			if (user) {
				getInterests();
			}
		}, [user, getInterests]);
	

	useEffect(() => {
		if (Object.keys(interests).length !== 0) {
			console.log('generating routines')
			generateWorkoutRoutines(prompt)
				.then((routineText) => {
					console.log('Generated Workout Routine:');
					console.log(routineText);
					const parsed = parseRoutineResponse(routineText);
					const sanitized = sanitizeRoutine(parsed, {
						isBodyweightMode: isBodyweight,
						allowedEquipmentLabels: selectedEquipments,
					});
					setRoutine(sanitized);
					setIsLoading(false);
				})
				.catch(err => {
					console.error('Failed to generate workout routine:', err);
					setIsLoading(false);
				});
		}
	}, [prompt, interests, isBodyweight, selectedEquipments, parseRoutineResponse, sanitizeRoutine])


	return (
		<>
			{routine && Object.keys(routine).length > 0 ? (
				<div className={style.GeneratedStrydsWrapper}>
					<div className={style.GeneratedStryds}>
						<div className={style.PageHeadingWrapper}>
							<span className={style.PageTitle}>
								We made this just for you! 💚
							</span>
						</div>

						<div className={style.RoutineWrapper}>
							<div className={style.RoutineDetailsWrapper}>
								<div className={style.RoutineNameWrapper}>
									<span className={style.RoutineName}>
										{routine.routineName}
									</span>
								</div>

								<div className={style.RoutineTimeWrapper}>
									<img src={timeIcon} alt="" className={style.TimeIcon} />
									<span className={style.RoutineText}>{routine.estimatedTimeMinutes} mins</span>
								</div>
							</div>

							<div className="line" />

							<span className={style.RoutineExplanation}>
								{routine.routineReason}
							</span>

							<div className="line" />

								<div className={style.RoutineWorkoutsWrapper}>
									{(routine.workouts || []).map((workout, index) => (
									<div className={style.RoutineWorkouts} key={index}>
										<span>{workout.workoutType}</span>
										<div className={style.RoutineWorkoutsNumber}>
												<span>{workout.workoutSets}</span>
												<span>{workout.workoutReps}</span>
											{/* <span>{routine.createdDate} {routine.routineCreationType}</span> */}
										</div>
									</div>
									))}
								</div>
						</div>
					</div>
					<div className={style.ButtonsWrapper}>
						<PrimaryButton
							text='Proceed'
							click={addStryds}
						// loading={isLoading}
						/>

						<SecondaryButton
							text='Regenerate'
							click={() => {
								window.location.reload(); // This will reload the page
							}}
						/>
					</div>
				</div>
			) : (
				<div className={style.RoutineGenerationText}>
					<img
						src={spinner}
						alt=""
						className={style.RoutineGenerationSpinner}
					/>
					<span>Generating your routine</span>
				</div>
			)}
		</>
	);

}



export default GeneratedStryds;